import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const NixlevTikTokAdCompliant: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Architectural Clean Light Palette (TikTok Whitelisted / B2B SaaS Style)
  const bgWarm = "#F4F2EC";
  const bgCard = "#FFFFFF";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#059669";
  const greenBright = "#10B981";
  const goldAccent = "#D4A346";
  const textDark = "#111827";
  const textMuted = "#4B5563";

  // Subtitle Timestamps (30s Exact)
  const SUBTITLES = [
    { from: 0, to: 165, text: "Masih jualan produk digital tapi kirim filenya manual lewat WhatsApp satu per satu?", highlight: "kirim filenya manual" },
    { from: 165, to: 260, text: "Otomatiskan tokomu pakai Nixlev.", highlight: "Nixlev" },
    { from: 260, to: 470, text: "Satu link bio profesional untuk pajang e-book, template Canva, preset foto, hingga jasamu.", highlight: "Satu link bio profesional" },
    { from: 470, to: 680, text: "Pembeli checkout via QRIS, file produk langsung terkirim otomatis ke WhatsApp & Email.", highlight: "terkirim otomatis ke WhatsApp & Email" },
    { from: 680, to: 785, text: "Lebih hemat waktu, bisnismu jadi jauh lebih rapi.", highlight: "Lebih hemat waktu" },
    { from: 785, to: 900, text: "Buka tokomu gratis hari ini di nixlev.com 🚀", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Springs Helper
  const sp = (delay: number, damping = 15.35, stiffness = 115) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Ambient breathing float (continuous alive feel)
  const floatY = Math.sin(frame / 22) * 5;

  // 1. Header Transition
  const headerSpring = sp(0, 14, 130);
  const headerY = interpolate(headerSpring, [0, 1], [-40, 0]);
  const headerOpacity = interpolate(headerSpring, [0, 1], [0, 1]);

  // 2. Base Browser Window 3D Entrance
  const browserSpring = sp(20, 15, 110);
  const browserY = interpolate(browserSpring, [0, 1], [80, 0]);
  const browserRotX = interpolate(browserSpring, [0, 1], [14, 5]);
  const browserScale = interpolate(browserSpring, [0, 1], [0.92, 1]);
  const browserOpacity = interpolate(browserSpring, [0, 1], [0, 1]);

  // 3. QRIS Pill Fly-in Transition
  const qrisSpring = sp(50, 13, 150);
  const qrisX = interpolate(qrisSpring, [0, 1], [100, 0]);
  const qrisY = interpolate(qrisSpring, [0, 1], [-40, 0]);
  const qrisRot = interpolate(qrisSpring, [0, 1], [12, 3]);
  const qrisScale = interpolate(qrisSpring, [0, 1], [0.75, 1]);
  const qrisOpacity = interpolate(qrisSpring, [0, 1], [0, 1]);

  // 4. Interactive Mouse Cursor
  const cursorProgress = interpolate(
    frame,
    [280, 340],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const cursorX = interpolate(cursorProgress, [0, 1], [680, 360]);
  const cursorY = interpolate(cursorProgress, [0, 1], [500, 260]);
  const cursorOpacity = interpolate(frame, [280, 295], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  
  // Click action at frame 345 - 355
  const isClicking = frame >= 345 && frame <= 355;
  const cursorScale = isClicking ? 0.85 : 1.0;

  // 5. Order Delivery Success Card (Breaking Bounds)
  const deliverySpring = sp(350, 13, 160);
  const deliveryY = interpolate(deliverySpring, [0, 1], [100, 0]);
  const deliveryX = interpolate(deliverySpring, [0, 1], [-50, 0]);
  const deliveryRot = interpolate(deliverySpring, [0, 1], [-10, -2]);
  const deliveryScale = interpolate(deliverySpring, [0, 1], [0.8, 1]);
  const deliveryOpacity = interpolate(deliverySpring, [0, 1], [0, 1]);

  // Progress Bar for File Dispatch (0% -> 100%)
  const dispatchProgressSpring = sp(375, 16, 100);
  const dispatchPercent = Math.min(100, Math.floor(interpolate(dispatchProgressSpring, [0, 1], [0, 100])));

  // 6. Value Proposition Card Transition (Frame 680)
  const valueSpring = sp(680, 14, 130);
  const valueY = interpolate(valueSpring, [0, 1], [40, 0]);
  const valueOpacity = interpolate(valueSpring, [0, 1], [0, 1]);

  // 7. CTA Button Transition & Arrow Bob (Frame 780)
  const ctaSpring = sp(780, 14, 140);
  const ctaScale = interpolate(ctaSpring, [0, 1], [0.85, 1]);
  const ctaOpacity = interpolate(ctaSpring, [0, 1], [0, 1]);
  const arrowBob = Math.sin(frame / 9) * 4;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgWarm,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* Background Architectural Grid Lines */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(0,0,0,0.035) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0,0,0,0.035) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* Audio Architecture */}
      <Audio src={staticFile("nixlev_tiktok_compliant.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />

      {/* Frame-Accurate Micro-SFX */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 20 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.5} />}
      {frame === 50 && <Audio src={staticFile("pop.mp3")} volume={0.6} />}
      {frame === 165 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 345 && <Audio src={staticFile("click.mp3")} volume={0.75} />}
      {frame === 350 && <Audio src={staticFile("ui-pop.mp3")} volume={0.7} />}
      {frame === 380 && <Audio src={staticFile("tick.mp3")} volume={0.5} />}
      {frame === 410 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}
      {frame === 680 && <Audio src={staticFile("ui-success.mp3")} volume={0.7} />}
      {frame === 785 && <Audio src={staticFile("chime.mp3")} volume={0.9} />}

      {/* Symmetrical Centered Canvas Container (Safe Zone Grounded) */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          top: 250,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* 1. Header Section */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 35,
            transform: `translateY(${headerY}px)`,
            opacity: headerOpacity,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              backgroundColor: bgCard,
              border: "1.5px solid #E5E7EB",
              color: textDark,
              padding: "10px 24px",
              borderRadius: 30,
              fontSize: 20,
              fontWeight: 800,
              marginBottom: 20,
              boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
            }}
          >
            {/* Lucide Sparkles Vector Icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={greenEmerald} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
              <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
            </svg>
            <span>CREATOR STOREFRONT AUTOMATION</span>
          </div>

          <h1
            style={{
              fontSize: 58,
              fontWeight: 900,
              lineHeight: 1.15,
              letterSpacing: "-0.03em",
            }}
          >
            Jual Produk Digital, <br />
            <span style={{ color: greenEmerald }}>File Terkirim Otomatis 24 Jam.</span>
          </h1>
        </div>

        {/* 2. Floating Stage (Breaking Bounds) */}
        <div
          style={{
            width: 860,
            height: 650,
            position: "relative",
            margin: "15px 0 35px 0",
          }}
        >
          {/* Breaking Bound Pill Top Right (Clean Light Mode) */}
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 0,
              backgroundColor: bgCard,
              border: `2px solid ${greenBright}`,
              borderRadius: 24,
              padding: "16px 26px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              boxShadow: "0 20px 45px rgba(16, 185, 129, 0.14), 0 4px 12px rgba(0,0,0,0.04)",
              zIndex: 20,
              transform: `translateX(${qrisX}px) translateY(${qrisY + floatY * 0.8}px) rotate(${qrisRot}deg) scale(${qrisScale})`,
              opacity: qrisOpacity,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                backgroundColor: "#ECFDF5",
                border: "1.5px solid #A7F3D0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Lucide Zap Vector Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={greenEmerald} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#065F46" }}>QRIS Instant Checkout</div>
              <div style={{ fontSize: 13, color: textMuted, fontWeight: 600 }}>Semua Bank & E-Wallet</div>
            </div>
          </div>

          {/* Central Elevated Base Browser Card */}
          <div
            style={{
              position: "absolute",
              top: 40,
              left: 30,
              right: 30,
              bottom: 40,
              backgroundColor: bgCard,
              borderRadius: 36,
              border: "2px solid #E5E7EB",
              boxShadow: "0 40px 100px rgba(0, 0, 0, 0.09), 0 10px 30px rgba(27, 59, 43, 0.05)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              transform: `perspective(1200px) rotateX(${browserRotX}deg) translateY(${browserY + floatY}px) scale(${browserScale})`,
              opacity: browserOpacity,
            }}
          >
            <div
              style={{
                height: 52,
                backgroundColor: "#FAFAFA",
                borderBottom: "1px solid #E5E7EB",
                padding: "0 24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <div
                style={{
                  backgroundColor: bgCard,
                  border: "1px solid #E5E7EB",
                  borderRadius: 10,
                  padding: "6px 24px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={greenEmerald} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <span>nixlev.com/@kreator</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: greenEmerald, display: "flex", alignItems: "center", gap: 4 }}>
                <span>Storefront ↗</span>
              </div>
            </div>
            <Img
              src={staticFile("nixlev_desktop.png")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "top center",
              }}
            />
          </div>

          {/* Interactive Floating Mouse Pointer */}
          <div
            style={{
              position: "absolute",
              left: cursorX,
              top: cursorY,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              gap: 8,
              filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.25))",
              opacity: cursorOpacity,
              transform: `scale(${cursorScale})`,
              transition: "transform 0.08s ease",
            }}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#111827" stroke="#FFFFFF" strokeWidth="2">
              <path d="M3 3l7 18 3-7 7-3L3 3z"/>
            </svg>
            <div
              style={{
                backgroundColor: isClicking ? "#047857" : greenEmerald,
                color: "white",
                padding: "8px 16px",
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 800,
                marginLeft: -5,
                marginTop: 25,
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 4px 15px rgba(5, 150, 105, 0.3)",
              }}
            >
              <span>Pembeli Checkout</span>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="7" y1="17" x2="17" y2="7"></line>
                <polyline points="7 7 17 7 17 17"></polyline>
              </svg>
            </div>
          </div>

          {/* Breaking Bound Order Delivery Success Card (100% Policy Compliant / Zero Scam Claims) */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: 530,
              backgroundColor: bgCard,
              border: `2px solid ${greenBright}`,
              borderRadius: 26,
              padding: "22px 28px",
              display: "flex",
              alignItems: "center",
              gap: 20,
              boxShadow: "0 30px 70px rgba(27, 59, 43, 0.18), 0 10px 25px rgba(0,0,0,0.06)",
              zIndex: 25,
              transform: `translateX(${deliveryX}px) translateY(${deliveryY + floatY * 0.6}px) rotate(${deliveryRot}deg) scale(${deliveryScale})`,
              opacity: deliveryOpacity,
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 20,
                backgroundColor: "#ECFDF5",
                border: "2px solid #A7F3D0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Lucide Package Check Vector Icon */}
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={greenEmerald} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m16 16 2 2 4-4"/>
                <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14"/>
                <path d="m3.3 7 8.7 5 8.7-5"/>
                <path d="M12 22V12"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 17, fontWeight: 800, color: "#065F46" }}>Pesanan #1048 Berhasil</span>
                <span style={{ fontSize: 13, color: greenEmerald, fontWeight: 800, background: "#ECFDF5", padding: "2px 8px", borderRadius: 6 }}>
                  Otomatis
                </span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: textDark, marginTop: 4 }}>
                E-Book & Template Kit
              </div>
              
              {/* Dispatch Progress Indicator */}
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: textMuted, fontWeight: 700, marginBottom: 4 }}>
                  <span>Kirim File ke WA & Email</span>
                  <span style={{ color: greenEmerald }}>{dispatchPercent}% Terkirim</span>
                </div>
                <div style={{ width: "100%", height: 6, backgroundColor: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${dispatchPercent}%`, height: "100%", backgroundColor: greenBright, transition: "width 0.1s ease" }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Value Banner Section */}
        <div
          style={{
            width: 860,
            backgroundColor: bgCard,
            border: "1.5px solid #E5E7EB",
            borderRadius: 26,
            padding: "24px 34px",
            textAlign: "center",
            boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
            marginBottom: 25,
            transform: `translateY(${valueY}px)`,
            opacity: valueOpacity,
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 800, color: textDark, lineHeight: 1.4 }}>
            Tanpa sewa hosting. Tanpa balas chat manual.<br />
            Semua transaksi & pengiriman produk <span style={{ color: greenEmerald }}>berjalan otomatis</span>!
          </div>
        </div>

        {/* 4. CTA Button Section */}
        <div
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            border: `2px solid ${goldAccent}`,
            borderRadius: 34,
            padding: "22px 56px",
            fontSize: 27,
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            gap: 16,
            boxShadow: "0 20px 45px rgba(27, 59, 43, 0.25)",
            transform: `scale(${ctaScale})`,
            opacity: ctaOpacity,
          }}
        >
          <Img src={staticFile("nixlev_logo.png")} style={{ width: 38, height: 38, objectFit: "contain" }} />
          <span>Buka Tokomu Gratis di nixlev.com</span>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: `translateX(${arrowBob}px)` }}
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </div>

        {/* Dynamic Subtitles in Grounded Safe Zone (Y = 1270 - 1440 px) */}
        {currentSub && (
          <div
            style={{
              position: "absolute",
              top: 1020,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              zIndex: 100,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(18, 24, 20, 0.94)",
                backdropFilter: "blur(14px)",
                padding: "16px 36px",
                borderRadius: 24,
                border: "1px solid rgba(255, 255, 255, 0.16)",
                boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
                maxWidth: 840,
                textAlign: "center",
                transform: `scale(${spring({
                  frame: frame - currentSub.from,
                  fps,
                  config: { damping: 14, stiffness: 180 },
                })})`,
              }}
            >
              <span style={{ fontSize: 27, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
                {currentSub.highlight ? (
                  <>
                    {currentSub.text.split(currentSub.highlight)[0]}
                    <span style={{ color: goldAccent }}>{currentSub.highlight}</span>
                    {currentSub.text.split(currentSub.highlight)[1]}
                  </>
                ) : (
                  currentSub.text
                )}
              </span>
            </div>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};
